import { useCallback, useMemo } from "react";

import { FeatureIslandCategoryQuestionForMain } from "@/components/features/_layouts/island/main-with-category/components/category-main-question";
import type { ForNodeType } from "@/components/features/_model/types";
import {
	CHECK_IN_END_LIST,
	CHECK_IN_START_LIST,
	CHECK_OUT_LIST,
} from "@/components/features/items/edit/_shared/constants/check-in-and-check-out";

import { FormItem } from "@/components/ui/form/_base/form";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { SelectField } from "@/components/ui/form/fields/select-field";

import { QuestionSectionWrapper } from "../../../system/question/_layouts/item-wrapper";

type Props = {
	checkInStart: ForNodeType<string>;
	checkInEnd: ForNodeType<string>;
	checkOut: ForNodeType<string>;

	onSetCheckInStart: (value: string) => void;
	onSetCheckInEnd: (value: string) => void;
	onSetCheckOut: (value: string) => void;

	guide?: string;
	isDisabled?: boolean;
};

export const QuestionCheckInAndCheckOut = ({
	checkInStart,
	onSetCheckInStart: _onSetCheckInStart,
	checkInEnd,
	onSetCheckInEnd: _onSetCheckInEnd,
	checkOut,
	onSetCheckOut: _onSetCheckOut,

	guide: guideProps,
	isDisabled = false,
}: Props) => {
	const checkInStartOptions = useMemo(() => {
		return CHECK_IN_START_LIST.map((item) => {
			return {
				id: item,
				label: item,
				value: item,
			} satisfies FormOptionType;
		});
	}, []);

	const handleChangeCheckInStart = useCallback(
		(value: string) => {
			_onSetCheckInStart(value);
		},
		[_onSetCheckInStart],
	);

	const checkInEndOptions = useMemo(() => {
		return CHECK_IN_END_LIST.map((item) => {
			return {
				id: item,
				label: item,
				value: item,
			} satisfies FormOptionType;
		}).filter((item) => checkInStart.value <= item.value);
	}, [checkInStart.value]);

	const handleChangeCheckInEnd = useCallback(
		(value: string) => {
			_onSetCheckInEnd(value);
		},
		[_onSetCheckInEnd],
	);

	const checkOutOptions = useMemo(() => {
		return CHECK_OUT_LIST.map((item) => {
			return {
				id: item,
				label: item,
				value: item,
			} satisfies FormOptionType;
		});
	}, []);

	const changeCheckOut = useCallback(
		(value: string) => {
			_onSetCheckOut(value);
		},
		[_onSetCheckOut],
	);

	const guide =
		guideProps ||
		`チェックイン・チェックアウトの時間を設定しましょう。 <br />
	ここでの設定が全ての予約にデフォルトで適用されます。 <br />
	プランごとに設定することも可能です。`;

	const isEdited = Boolean(checkInStart.isEdited || checkInEnd.isEdited || checkOut.isEdited);
	const hasError = Boolean(checkInStart.error || checkInEnd.error || checkOut.error);

	return (
		<FeatureIslandCategoryQuestionForMain
			required={true}
			title={"チェックイン・チェックアウトの時間"}
			guide={guide}
			isEdited={isEdited}
			hasError={hasError}
			inputChildren={
				<>
					<FormItem>
						<QuestionSectionWrapper>
							<div className="space-y-2">
								<p>チェックイン</p>
								<div className="flex items-center gap-x-2">
									<SelectField
										value={checkInStart.value}
										options={checkInStartOptions}
										handleValueChange={handleChangeCheckInStart}
										placeholder="選択"
										errorMessage={checkInStart.error}
										disabled={isDisabled}
									/>
									<p>〜</p>
									<SelectField
										value={checkInEnd.value}
										options={checkInEndOptions}
										handleValueChange={handleChangeCheckInEnd}
										placeholder="選択"
										errorMessage={checkInEnd.error}
										disabled={isDisabled}
									/>
								</div>
							</div>
						</QuestionSectionWrapper>
					</FormItem>
					<FormItem>
						<QuestionSectionWrapper>
							<div className="space-y-2">
								<p>チェックアウト</p>
								<div className="flex items-center gap-x-2">
									<SelectField
										value={checkOut.value}
										options={checkOutOptions}
										handleValueChange={changeCheckOut}
										errorMessage={checkOut.error}
										placeholder="選択"
										disabled={isDisabled}
									/>
									<p>まで</p>
								</div>
							</div>
						</QuestionSectionWrapper>
					</FormItem>
				</>
			}
		/>
	);
};

QuestionCheckInAndCheckOut.displayName = "QuestionCheckInAndCheckOut";
